import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { BrowserRouter, Routes, Route, NavLink } from "react-router-dom";
import Calendar from "./components/Calendar";
import AreaView from "./components/AreaView";
import TaskForm from "./components/TaskForm";
import AppointmentForm from "./components/AppointmentForm";
import ConfirmDialog from "./components/ConfirmDialog";
import Roadmap from "./components/Roadmap";
import Time from "./components/Time";
import EmployeeAdminPage from "./components/EmployeeAdminPage";
import ProjectsPage from "./components/ProjectsPage";
import LoginPage from "./components/LoginPage";
import ComputerMenu from "./components/ComputerMenu";
import {
  fetchTasks, createTask, updateTask, deleteTask, moveTask, toggleTask, reorderTasks,
  fetchAppointments, createAppointment, updateAppointment, deleteAppointment,
  fetchEmployees, fetchAreas,
  fetchUsers, createUser, updateUser, deleteUser,
  getStoredUser, logout, refreshStoredUser, initAuthListener, refreshSession,
  syncAllOutlook,
  getWsUrl, getWsToken,
  fetchRoadmapItems,
  fetchVacations,
} from "./api";
import { supabase } from "./supabase";
import { parseISODateLocal } from "./lib/dates";
import { isWorkingStudentAt } from "./lib/timeMath";
import { applyAreaConfig, DEFAULT_FILTERS } from "./constants";
import "./App.css";

export default function App() {
  const [user, setUser] = useState(getStoredUser);
  const [authChecked, setAuthChecked] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [editingTask, setEditingTask] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [appointmentFormDate, setAppointmentFormDate] = useState("");
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Mobile only: the nav collapses into a hamburger drawer so the header stays
  // one row (a bottom tab bar would clash with the filter button bottom-right).
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMobileNavOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileNavOpen]);
  const [areas, setAreas] = useState([]);
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [calendarTasks, setCalendarTasks] = useState([]);
  const [calendarAppointments, setCalendarAppointments] = useState([]);
  const calendarRangeRef = useRef({ from: null, to: null });
  // tracks whether a mouse press started on the modal backdrop itself —
  // prevents closing when a text selection is dragged out of the modal
  const overlayDownRef = useRef(false);

  const handleOverlayMouseDown = (e) => {
    overlayDownRef.current = e.target === e.currentTarget;
  };
  const overlayClose = (e, close) => {
    if (e.target === e.currentTarget && overlayDownRef.current) close();
  };

  // Dirty guard for the task/appointment forms: a backdrop tap (easy to hit by
  // accident, especially on mobile) only closes directly while the form is
  // untouched — with edits it asks first. The forms report via onDirtyChange.
  const formDirtyRef = useRef(false);
  const [confirmDiscardClose, setConfirmDiscardClose] = useState(null); // () => void
  const guardedClose = (close) => {
    if (formDirtyRef.current) setConfirmDiscardClose(() => close);
    else close();
  };
  // Stable identity: the forms' dirty-report effects depend on this prop — an
  // inline arrow would re-run them (cleanup inclusive) on every App render.
  const handleFormDirty = useCallback((d) => { formDirtyRef.current = d; }, []);

  const isAdmin = user?.role === "admin";

  // Initialize Supabase auth listener + verify token on mount
  useEffect(() => {
    initAuthListener();
    const storedUser = getStoredUser();
    if (!storedUser) {
      setAuthChecked(true);
      setLoading(false);
      return;
    }
    // Adopt the freshly fetched profile — the cached one is only written at login
    // and would otherwise keep a stale name for the rest of the session.
    const adopt = (u) => { setUser(u); setAuthChecked(true); };
    refreshSession()
      .then((valid) => {
        if (valid) return refreshStoredUser().then(adopt);
        // Refresh failed — check if Supabase still has a session (e.g. token not yet expired)
        return supabase.auth.getSession().then(({ data }) => {
          if (data?.session) {
            localStorage.setItem("token", data.session.access_token);
            return refreshStoredUser().then(adopt);
          }
          throw new Error("No valid session");
        });
      })
      .catch(() => {
        logout();
        setUser(null);
        setAuthChecked(true);
      });
  }, []);

  // Monotonic request counters: rapid-fire reloads (WebSocket bursts, quick
  // edits) can resolve out of order — without the guard the OLDEST response
  // could land last and overwrite newer data. Only the newest request wins.
  const tasksReqSeq = useRef(0);
  const apptsReqSeq = useRef(0);
  const loadTasks = useCallback(async () => {
    const seq = ++tasksReqSeq.current;
    try {
      const data = await fetchTasks();
      if (seq !== tasksReqSeq.current) return; // superseded
      setTasks(data);
      setError(null);
      // keep the calendar's task list in sync too (mirrors loadAppointments)
      const range = calendarRangeRef.current;
      if (range.from && range.to) {
        const rangeData = await fetchTasks(range.from, range.to);
        if (seq !== tasksReqSeq.current) return;
        setCalendarTasks(prev => {
          const outside = prev.filter(t => !t.calendar_date || t.calendar_date < range.from || t.calendar_date > range.to);
          return [...outside, ...rangeData];
        });
      }
    } catch (e) {
      console.error(e);
      if (seq === tasksReqSeq.current) setError("Fehler beim Laden der Tasks");
    }
  }, []);

  const loadAppointments = useCallback(async () => {
    const seq = ++apptsReqSeq.current;
    try {
      const data = await fetchAppointments();
      if (seq !== apptsReqSeq.current) return; // superseded
      setAppointments(data);
      const range = calendarRangeRef.current;
      if (range.from && range.to) {
        const rangeData = await fetchAppointments(range.from, range.to);
        if (seq !== apptsReqSeq.current) return;
        setCalendarAppointments(prev => {
          const outside = prev.filter(t => t.date < range.from || t.date > range.to);
          return [...outside, ...rangeData];
        });
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  // Same seq-guard idea as loadTasks/loadAppointments: without it a slow range
  // load could resolve AFTER a fresher WS-triggered reload and resurrect stale
  // rows (e.g. a task deleted in the meantime). Returns whether the write won.
  const calendarReqSeq = useRef(0);
  const loadCalendarData = useCallback(async (from, to) => {
    const seq = ++calendarReqSeq.current;
    try {
      const [newTasks, newAppts] = await Promise.all([
        fetchTasks(from, to),
        fetchAppointments(from, to),
      ]);
      if (seq !== calendarReqSeq.current) return false; // superseded
      setCalendarTasks(prev => {
        const outside = prev.filter(t => !t.calendar_date || t.calendar_date < from || t.calendar_date > to);
        return [...outside, ...newTasks];
      });
      setCalendarAppointments(prev => {
        const outside = prev.filter(t => t.date < from || t.date > to);
        return [...outside, ...newAppts];
      });
      return true;
    } catch (e) {
      console.error("Calendar data load failed:", e);
      if (seq === calendarReqSeq.current) setError("Fehler beim Laden des Kalenderzeitraums");
      return false;
    }
  }, []);

  const handleCalendarRangeChange = useCallback((from, to) => {
    const prev = calendarRangeRef.current;
    const needsLoad = !prev.from || from < prev.from || to > prev.to;
    if (needsLoad) {
      const newFrom = prev.from && from > prev.from ? prev.from : from;
      const newTo = prev.to && to < prev.to ? prev.to : to;
      // Mark the range as loaded only on SUCCESS — marking it up front meant a
      // failed fetch left the weeks permanently empty (needsLoad never fired
      // again for them).
      loadCalendarData(newFrom, newTo).then((ok) => {
        if (ok) calendarRangeRef.current = { from: newFrom, to: newTo };
      });
    }
  }, [loadCalendarData]);

  const loadEmployees = useCallback(async () => {
    try {
      // Include archived so departed staff's vacation resolves to a name in the
      // calendar/roadmap; pages filter to active people themselves where needed.
      const data = await fetchEmployees(true);
      setEmployees(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const data = await fetchUsers();
      setUsers(data);
    } catch (e) {
      console.error(e);
    }
  }, [isAdmin]);


  const loadRoadmapItems = useCallback(async () => {
    try {
      const data = await fetchRoadmapItems();
      setRoadmapItems(data);
    } catch (e) {
      console.warn("Roadmap load failed:", e);
    }
  }, []);

  const loadVacations = useCallback(async () => {
    try {
      const data = await fetchVacations();
      setVacations(data);
    } catch (e) {
      // ignore — vacations are non-critical for non-workforce views
    }
  }, []);

  const loadAreas = useCallback(async () => {
    try {
      const data = await fetchAreas();
      applyAreaConfig(data);
      const mapped = data.map((k) => ({ key: k.key, label: k.label, abbreviation: k.abbreviation, bg: k.bg, border: k.border_color, text: k.text_color, admin_only: k.admin_only }));
      // Apply user's custom order from localStorage
      try {
        const raw = localStorage.getItem("areaOrder");
        if (raw) {
          const order = JSON.parse(raw);
          const ordered = order.map((key) => mapped.find((k) => k.key === key)).filter(Boolean);
          const missing = mapped.filter((k) => !order.includes(k.key));
          setAreas([...ordered, ...missing]);
          return;
        }
      } catch {}
      setAreas(mapped);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (!user || !authChecked) return;
    setLoading(true);
    Promise.all([loadAreas(), loadTasks(), loadAppointments(), loadEmployees(), loadUsers(), loadRoadmapItems(), loadVacations()])
      .then(() => {
        // Auto-sync Outlook calendars in background
        syncAllOutlook().then(() => loadAppointments()).catch(e => console.warn("Outlook sync failed:", e));
      })
      .finally(() => setLoading(false));
  }, [user, authChecked, loadAreas, loadTasks, loadAppointments, loadEmployees, loadUsers, loadRoadmapItems, loadVacations]);

  // Vacation mutations fire a local event (api.js) on top of the WebSocket
  // broadcast, so the "offene Urlaubsanträge" badge updates in the same tab even
  // when the socket doesn't come through.
  useEffect(() => {
    const onWorkforceChanged = () => loadVacations();
    window.addEventListener("workforce-changed", onWorkforceChanged);
    return () => window.removeEventListener("workforce-changed", onWorkforceChanged);
  }, [loadVacations]);

  // WebSocket for real-time updates
  useEffect(() => {
    if (!user || !authChecked) return;
    let ws;
    let reconnectTimer;
    let isFirstConnect = true;
    const connect = () => {
      ws = new WebSocket(getWsUrl());
      ws.onopen = () => {
        const token = getWsToken();
        if (token) ws.send(JSON.stringify({ token }));
        if (!isFirstConnect) {
          loadTasks(); loadAppointments(); loadAreas(); loadUsers(); loadEmployees(); loadRoadmapItems(); loadVacations();
          window.dispatchEvent(new Event("planning:computers"));
        }
        isFirstConnect = false;
      };
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "ping") { ws.send(JSON.stringify({ type: "pong" })); return; }
          // loadTasks already refreshes the loaded calendar range itself — the
          // extra loadCalendarData here used to fire a third GET per event.
          if (msg.type === "tasks") loadTasks();
          // loadAppointments refreshes the loaded calendar range itself (seq-
          // guarded) — the extra loadCalendarData was a redundant, UNguarded
          // fetch whose stale answer could overwrite the guarded one.
          if (msg.type === "appointments") loadAppointments();
          if (msg.type === "areas") loadAreas();
          if (msg.type === "users") { loadUsers(); loadEmployees(); }
          if (msg.type === "roadmap") loadRoadmapItems();
          if (msg.type === "workforce") loadVacations();
          // Rechner lädt ComputerMenu selbst (mit eigenen Rechten), hier nur der Anstoß
          if (msg.type === "computers") window.dispatchEvent(new Event("planning:computers"));
        } catch {}
      };
      ws.onclose = () => {
        // Only reconnect while this effect is alive — the cleanup's ws.close()
        // fires onclose asynchronously, which used to schedule a fresh timer
        // AFTER unmount/logout and leave a zombie socket polling forever.
        if (!disposed) reconnectTimer = setTimeout(connect, 3000);
      };
      ws.onerror = () => {
        ws.close();
      };
    };
    let disposed = false;
    connect();
    return () => {
      disposed = true;
      clearTimeout(reconnectTimer);
      if (ws) ws.close();
    };
  }, [user, authChecked, loadTasks, loadAppointments, loadAreas, loadUsers, loadEmployees, loadRoadmapItems, loadVacations]);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    await logout();
    setUser(null);
    // Reset ALL data state, not just the obvious three — otherwise a user switch
    // in the same tab leaks the previous session's data (e.g. a non-admin would
    // keep the admin's full users list, since loadUsers never overwrites it).
    setTasks([]);
    setAppointments([]);
    setEmployees([]);
    setUsers([]);
    setVacations([]);
    setRoadmapItems([]);
    setCalendarTasks([]);
    setCalendarAppointments([]);
    setAreas([]);
    setFilters(DEFAULT_FILTERS);
    setEditingTask(null);
    setShowForm(false);
    setShowAppointmentForm(false);
    setEditingAppointment(null);
    calendarRangeRef.current = { from: null, to: null };
  };

  const matchesFilters = useCallback((t) => {
    if (filters.status && filters.status.length > 0) {
      const taskStatus = t.completed ? "completed" : "open";
      if (!filters.status.includes(taskStatus)) return false;
    }
    if (filters.person && filters.person.length > 0 && !(t.person_ids || []).some((pid) => filters.person.includes(String(pid)))) return false;
    if (filters.prio && filters.prio.length > 0 && !filters.prio.includes(t.prio)) return false;
    if (filters.area && filters.area.length > 0 && !filters.area.includes(t.area)) return false;
    return true;
  }, [filters]);

  const filteredTasks = useMemo(() => tasks.filter(matchesFilters), [tasks, matchesFilters]);
  const filteredCalendarTasks = useMemo(() => calendarTasks.filter(matchesFilters), [calendarTasks, matchesFilters]);

  const calendarRoadmapItems = useMemo(() => {
    // Roadmap items relevant for calendar: all _mitarbeiter entries except Semesterferien
    const real = roadmapItems.filter(
      (item) => (item.group_key === "_mitarbeiter" || item.group_key === "_events") && item.title !== "Semesterferien" && item.date && item.date_until
    );
    // Only approved vacations show up in the calendar (pending requests stay hidden)
    const synthetic = vacations
      .filter((v) => v.status === "approved" && v.start_date && v.end_date)
      .map((v) => {
        const emp = employees.find((e) => e.id === v.user_id);
        // Werkstudenten nehmen Urlaub in ganzen 8-h-Tagen — für sie entfällt
        // der "halb"-Zusatz. Entscheidend ist die PHASE am Urlaubsbeginn (die
        // Rolle ist nur noch Berechtigung: admin | employee). Admins haben die
        // volle users-Liste; ein Nicht-Admin sieht ohnehin nur eigenen Urlaub
        // und nutzt sein eigenes Profil (Phasen kommen aus /auth/me).
        const owner = users.find((u) => u.id === v.user_id)
          || (v.user_id === user?.id ? user : null);
        return {
          id: `vac-${v.id}`,
          group_key: "_mitarbeiter",
          title: "Urlaub",
          color: "#4a4366",
          date: v.start_date,
          date_until: v.end_date,
          person_ids: [v.user_id],
          persons: emp ? [emp] : [],
          synthetic: true,
          status: v.status,
          half_day: v.half_day || false,
          working_student: owner ? isWorkingStudentAt(owner, parseISODateLocal(v.start_date)) : false,
        };
      });
    return [...real, ...synthetic];
  }, [roadmapItems, vacations, employees, users, user]);

  const pendingVacationCount = vacations.filter((v) => v.status === "requested").length;

  const handleCreate = async (task) => {
    await createTask(task);
    setShowForm(false);
    loadTasks();
  };

  const handleUpdate = async (task) => {
    try {
      await updateTask(editingTask.id, { ...task, version: editingTask.version });
      setEditingTask(null);
      setShowForm(false);
    } catch (e) {
      if (e.status === 409) {
        setError(e.message);
        setEditingTask(null);
        setShowForm(false);
      } else {
        throw e;
      }
    }
    loadTasks();
  };

  const handleDelete = async (id) => {
    try {
      await deleteTask(id);
    } catch (e) {
      console.error("Delete failed:", e);
    }
    setEditingTask(null);
    setShowForm(false);
    loadTasks();
  };

  // Merge the returned task into both task states (like handleQuickUpdate):
  // move/toggle bump the version server-side now, so a follow-up write with the
  // pre-move version would otherwise bounce off with a bogus 409.
  const mergeTask = useCallback((updated) => {
    if (!updated?.id) return;
    setTasks((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
    setCalendarTasks((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
  }, []);

  const taskVersionOf = (id) =>
    (tasks.find((t) => t.id === id) || calendarTasks.find((t) => t.id === id))?.version;

  const handleMove = async (id, newDate) => {
    try {
      mergeTask(await moveTask(id, newDate, taskVersionOf(id)));
    } catch (e) {
      if (e.status === 409) setError(e.message); else throw e;
    }
    loadTasks();
  };

  const handleToggle = async (id) => {
    try {
      mergeTask(await toggleTask(id, taskVersionOf(id)));
    } catch (e) {
      if (e.status === 409) setError(e.message); else throw e;
    }
    loadTasks();
  };

  const handleReorder = async (items) => {
    await reorderTasks(items);
    loadTasks();
  };

  const handleQuickUpdate = async (id, data) => {
    try {
      const task = tasks.find((t) => t.id === id);
      const updated = await updateTask(id, { ...data, version: task?.version });
      // Merge the fresh version into state RIGHT AWAY — two quick-menu clicks
      // in a row otherwise send the second request with the stale version and
      // bounce off with a bogus 409 before loadTasks lands. calendarTasks feeds
      // the calendar's quick menus, so it needs the merge just as much.
      if (updated?.id) {
        setTasks((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
        setCalendarTasks((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
      }
    } catch (e) {
      if (e.status === 409) {
        setError(e.message);
      } else {
        throw e;
      }
    }
    loadTasks();
  };

  const handleDuplicate = async (task) => {
    const dup = {
      name: task.name,
      person_ids: task.person_ids || [],
      area: task.area,
      category: task.category,
      deadline: task.deadline,
      prio: task.prio,
      calendar_date: task.calendar_date,
      notes: task.notes,
    };
    await createTask(dup);
    loadTasks();
  };

  const handleQuickDelete = async (id) => {
    try {
      await deleteTask(id);
    } catch (e) {
      console.error("Delete failed:", e);
    }
    loadTasks();
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setShowForm(true);
  };

  const handleNewTask = (defaults) => {
    setEditingTask(null);
    setShowForm(true);
    if (defaults) {
      setEditingTask(typeof defaults === "string" ? { calendar_date: defaults } : defaults);
    }
  };

  const handleAddAppointment = (date) => {
    setEditingAppointment(null);
    setAppointmentFormDate(date);
    setShowAppointmentForm(true);
  };

  const handleEditAppointment = (appointment) => {
    setEditingAppointment(appointment);
    setAppointmentFormDate(appointment.date);
    setShowAppointmentForm(true);
  };

  const handleAppointmentSubmit = async (data) => {
    try {
      if (editingAppointment) {
        await updateAppointment(editingAppointment.id, { ...data, version: editingAppointment.version });
      } else {
        await createAppointment(data);
      }
      setShowAppointmentForm(false);
      setEditingAppointment(null);
    } catch (e) {
      if (e.status === 409) {
        setError(e.message);
        setShowAppointmentForm(false);
        setEditingAppointment(null);
      } else {
        throw e;
      }
    }
    loadAppointments();
  };

  const handleMoveAppointment = async (id, newDate) => {
    try {
      const appointment = appointments.find((t) => t.id === id);
      const updated = await updateAppointment(id, { date: newDate, version: appointment?.version });
      // Merge the fresh version right away (like handleQuickUpdate) — two quick
      // drags of the same appointment otherwise send the stale version and
      // bounce off with a bogus 409 before loadAppointments lands.
      if (updated?.id) {
        setAppointments((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
        setCalendarAppointments((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
      }
    } catch (e) {
      if (e.status === 409) {
        setError(e.message);
      } else {
        throw e;
      }
    }
    loadAppointments();
  };

  const handleDeleteAppointment = async (id) => {
    await deleteAppointment(id);
    loadAppointments();
  };

  const handleDuplicateAppointment = async (appointment) => {
    const { id, version, outlook_id, is_all_day, location, user_id, ...data } = appointment;
    await createAppointment(data);
    loadAppointments();
  };

  const handleSyncAllOutlook = async () => {
    await syncAllOutlook();
    await loadAppointments();
  };

  const handleCreateUser = async (username, password, role, color, abbreviation, email) => {
    // Return the created user — EmployeeAdminPage selects the new person from it.
    const created = await createUser(username, password, role, color, abbreviation, email);
    loadUsers();
    loadEmployees();
    return created;
  };

  const handleUpdateUser = async (id, data) => {
    const u = users.find((u) => u.id === id);
    const updated = await updateUser(id, { ...data, version: u?.version });
    // Merge the fresh version right away — rapid consecutive edits (KW-Soll
    // cells, toggles) otherwise reuse the stale version from the not-yet-
    // refetched users state and lose the first change or bounce with a 409.
    if (updated?.id) {
      setUsers((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
    }
    loadUsers();
    loadEmployees();
    if (data.color || data.username) loadTasks();
    if (data.outlook_enabled !== undefined) loadAppointments();
  };

  const handleDeleteUser = async (id) => {
    await deleteUser(id);
    loadUsers();
    loadEmployees();
  };

  // Show nothing while checking auth
  if (!authChecked) {
    return (
      <div className="app-loading">
        <div className="app-spinner" />
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <BrowserRouter>
      <div className="app">
        <header className="app-header">
          {/* Sibling of .header-brand on purpose: inside it the button would join
              the brand's baseline alignment and push "PLANNING" off the logo. */}
          <button
            type="button"
            className="header-menu-btn"
            onClick={() => setMobileNavOpen((v) => !v)}
            aria-label={mobileNavOpen ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={mobileNavOpen}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              {mobileNavOpen
                ? <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>
                : <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>}
            </svg>
          </button>
          <div className="header-brand">
            <img src="/relticc_Logo_white.svg" alt="relticc" className="header-logo" />
            <h1>PLANNING</h1>
          </div>
          {/* Clicks bubble up from the links, so tapping any entry closes the drawer. */}
          <div
            className={`header-nav-wrap ${mobileNavOpen ? "is-open" : ""}`}
            onClick={() => setMobileNavOpen(false)}
          >
            <nav className="header-nav">
              <NavLink to="/" end className={({ isActive }) => `nav-link ${isActive ? "nav-active" : ""}`}>
                Kalender
              </NavLink>
              <NavLink to="/liste" className={({ isActive }) => `nav-link ${isActive ? "nav-active" : ""}`}>
                Liste
              </NavLink>
              <NavLink to="/time" className={({ isActive }) => `nav-link ${isActive ? "nav-active" : ""}`}>
                Zeit
              </NavLink>
            </nav>
            {isAdmin && (
              <nav className="header-nav header-nav-admin">
                <NavLink to="/roadmap" className={({ isActive }) => `nav-link ${isActive ? "nav-active" : ""}`}>
                  Roadmap
                </NavLink>
                <NavLink to="/mitarbeiter" className={({ isActive }) => `nav-link ${isActive ? "nav-active" : ""}`}>
                  Personal
                  {pendingVacationCount > 0 && (
                    <span className="nav-badge" title={`${pendingVacationCount} offene Urlaubsanträge`}>!</span>
                  )}
                </NavLink>
                <NavLink to="/projekte" className={({ isActive }) => `nav-link ${isActive ? "nav-active" : ""}`}>
                  Projekte
                </NavLink>
              </nav>
            )}
          </div>
          <div className="header-right">
            <ComputerMenu isAdmin={isAdmin} users={users} currentUserId={user?.id} />
            <button className="header-logout-btn" onClick={handleLogout} title="Abmelden">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        </header>

        {mobileNavOpen && (
          <div className="mobile-backdrop" onClick={() => setMobileNavOpen(false)} />
        )}

        {error && (
          <div className="app-error-bar">
            {error}
            <button onClick={() => setError(null)}>&times;</button>
          </div>
        )}

        {loading && (
          <div className="app-loading-bar">Laden...</div>
        )}

        {showForm && (
          <div
            className="modal-overlay"
            onMouseDown={handleOverlayMouseDown}
            onClick={(e) => overlayClose(e, () => guardedClose(() => { setShowForm(false); setEditingTask(null); }))}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              {/* Full employees list on purpose: the form itself keeps archived
                  people visible when they are already assigned. */}
              <TaskForm
                task={editingTask}
                onDirtyChange={handleFormDirty}
                employees={employees}
                areas={areas}
                onSubmit={editingTask?.id ? handleUpdate : handleCreate}
                onCancel={() => { setShowForm(false); setEditingTask(null); }}
                onDelete={handleDelete}
              />
            </div>
          </div>
        )}

        {showAppointmentForm && (
          <div
            className="modal-overlay"
            onMouseDown={handleOverlayMouseDown}
            onClick={(e) => overlayClose(e, () => guardedClose(() => { setShowAppointmentForm(false); setEditingAppointment(null); }))}
          >
            <div className="modal-content modal-no-clip" onClick={(e) => e.stopPropagation()}>
              <AppointmentForm
                date={appointmentFormDate}
                onDirtyChange={handleFormDirty}
                employees={employees}
                currentUser={user}
                isAdmin={isAdmin}
                existingAppointments={appointments}
                editingAppointment={editingAppointment}
                onSubmit={handleAppointmentSubmit}
                onCancel={() => { setShowAppointmentForm(false); setEditingAppointment(null); }}
                onDelete={editingAppointment ? async () => { await handleDeleteAppointment(editingAppointment.id); setShowAppointmentForm(false); setEditingAppointment(null); } : undefined}
              />
            </div>
          </div>
        )}

        {confirmDiscardClose && (
          <ConfirmDialog
            message="Ungespeicherte Änderungen verwerfen?"
            confirmLabel="Verwerfen"
            onConfirm={() => { const close = confirmDiscardClose; setConfirmDiscardClose(null); formDirtyRef.current = false; close(); }}
            onCancel={() => setConfirmDiscardClose(null)}
          />
        )}




        <Routes>
          <Route
            path="/"
            element={
              <Calendar
                tasks={filteredCalendarTasks}
                appointments={calendarAppointments}
                roadmapItems={calendarRoadmapItems}
                onRangeChange={handleCalendarRangeChange}
                employees={employees}
                areas={areas}
                onMove={handleMove}
                onEdit={handleEdit}
                onNewTask={handleNewTask}
                onToggle={handleToggle}
                onReorder={handleReorder}
                onAddAppointment={handleAddAppointment}
                onDeleteAppointment={handleDeleteAppointment}
                onDuplicateAppointment={handleDuplicateAppointment}
                onMoveAppointment={handleMoveAppointment}
                onEditAppointment={handleEditAppointment}
                onQuickUpdate={handleQuickUpdate}
                onDuplicate={handleDuplicate}
                onDelete={handleQuickDelete}
                filters={filters}
                onFiltersChange={setFilters}
              />
            }
          />
          <Route
            path="/liste"
            element={
              <AreaView
                tasks={filteredTasks}
                allTasks={tasks}
                appointments={appointments}
                employees={employees}
                areas={areas}
                onEdit={handleEdit}
                onQuickUpdate={handleQuickUpdate}
                onDuplicate={handleDuplicate}
                onDelete={handleQuickDelete}
                onNewTask={handleNewTask}
                onAddAppointment={handleAddAppointment}
                onEditAppointment={handleEditAppointment}
                onDeleteAppointment={handleDeleteAppointment}
                onDuplicateAppointment={handleDuplicateAppointment}
                filters={filters}
                onFiltersChange={setFilters}
                isAdmin={isAdmin}
              />
            }
          />
          {isAdmin && (
            <Route
              path="/roadmap"
              element={
                <Roadmap />
              }
            />
          )}
          <Route
            path="/time"
            element={
              // Zeit tab is always self-scoped — even admins only see their own entries here.
              <Time isAdmin={isAdmin} />
            }
          />
          {isAdmin && (
            <Route
              path="/mitarbeiter"
              element={
                <EmployeeAdminPage
                  users={users}
                  appointments={appointments}
                  employees={employees}
                  onCreateUser={handleCreateUser}
                  onUpdateUser={handleUpdateUser}
                  onDeleteUser={handleDeleteUser}
                  onSyncOutlook={handleSyncAllOutlook}
                />
              }
            />
          )}
          {isAdmin && (
            <Route path="/projekte" element={<ProjectsPage />} />
          )}
        </Routes>
      </div>
    </BrowserRouter>
  );
}
